/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, ViewChild } from "@angular/core";

import { fromEvent, Subject, Subscription } from "rxjs";

import { ObjectInstance } from "app/models/object-instance";
import { ZoneInstance } from "app/models/zone-instance";
import { ObjectService } from "app/services/object.service";
import { EventInstance } from "app/models/event-instance";
import {
  EVENT_PROP_Asset,
  EVENT_PROP_Person,
} from "app/app-constants";
import { ImageService } from "app/services/image.service";
import { ActivatedRoute } from "@angular/router";
import { InstanceFunctionDialogComponent } from "app/components/locate/instance-function-dialog/instance-function-dialog.component";
import { InstanceDataDialogComponent } from "app/components/locate/instance-data-dialog/instance-data-dialog.component";
import { InstanceClickedEvent, NewMapComponent } from "app/components/map/new-map.component";
import { PagedResults } from "app/models/paged-results";
import { map, debounceTime} from "rxjs/operators";
import { ZoneService } from "app/services/zone.service";
import { ObjectCategory } from "app/models/object-category";
import { MonitorZoneWebSocketService } from "app/services/monitor-zone-web-socket-service";
import { EventUuid_AssetEnteredZone, EventUuid_AssetExitedZone, EventUuid_AssetMovement, EventUuid_PersonEnteredZone, EventUuid_PersonExitedZone, EventUuid_PersonMovement } from "app/event-constants";
import { ObjectInstanceWebSocketService } from "app/services/object-instance-websocket.service";
import { ObjectProperty } from "app/models/object-property";
import { PropertyType } from "app/models/property-type";

declare var $: any;

/** 
 * Number of Zones to Load / Display on initial display and when searching.  
 * This is the maximum number of Zones that will be shown to the User for selecting. 
 * */
export const MonitorZone_ResultsPerPage: number = 50;

/**
 * "Monitor Zone" Page Component
 * 
 * Initially displays a List of Zones for the User to select from.  When selected, the Assets / People
 * in the Zone are loaded and displayed in the RHS Lists, and the selected Zone's Location Floorplan is
 * displayed along with the Zone, while the Zone is zoomed to. The User can then select the Assets / People
 * on the LHS to track / display on the RHS. 
 */
@Component({
  templateUrl: './monitor-zone.component.html',
  styleUrls: ['./monitor-zone.component.css']
})
export class MonitorZoneComponent implements OnInit, OnDestroy {

  /** MapComponent for viewing the location chosen inside the Locate Component */
  @ViewChild('locateMap') locateMap: NewMapComponent;

  /** Reference to the AssetFunctionDialog used to execute Function Action(s) on Assets */
  @ViewChild(InstanceFunctionDialogComponent) instanceFunctionDialogComponent: InstanceFunctionDialogComponent;

  /** Reference to the AssetDataDialog used to execute the "Edit Data" Action on Assets */
  @ViewChild(InstanceDataDialogComponent) instanceDataDialogComponent: InstanceDataDialogComponent;

  /** Available Zone Options displayed to the User */
  displayedZones: ZoneInstance[] = [];

  /** Assets in the selected Zone. Displayed in the LHS for selection to be tracked */
  assetsInZone: ObjectInstance[] = [];

  /** Assets in the selected Zone that should be displayed in the LHS List.  This array takes into account any search operations being done on the Assets in the Zone. */
  assetsInList: ObjectInstance[] = [];

  /** People in the selected Zone. Displayed in the LHS for selection to be tracked */
  peopleInZone: ObjectInstance[] = [];

  /** People in the selected Zone that should be displayed in the LHS List.  This array takes into account any search operations being done on the People in the Zone. */
  peopleInList: ObjectInstance[] = [];

  /** Zone instance selected by the User that is currently being displayed / monitored */
  selectedZone: ZoneInstance = null;

  /** Boolean Flag used to indicate if a Zone is currently being selected / loaded / displayed. */
  isSelectingZone = false;

  /** Holds the Asset's being toggled from being "TRACKED" <=> "NOT TRACKED".  Used to disable the checkbox while it is being toggled. */
  assetsBeingToggled: Map<number, ObjectInstance> = new Map();

  /** Holds the Asset's which are currently being tracked / have open WebSocket connections. */
  assetsBeingTracked: Map<number, WsConnection> = new Map();

  /** Boolean flag used to indicate if the "Select/Unselect All Assets" process is running.  Used to disable the Asset UI checkboxes. */
  isSelectingAllAssets = false;

  /** Holds the People being toggled from being "TRACKED" <=> "NOT TRACKED".  Used to disable the checkbox while it is being toggled. */
  peopleBeingToggled: Map<number, ObjectInstance> = new Map();

  /** Holds the People which are currently being tracked / have open WebSocket connections. */
  peopleBeingTracked: Map<number, WsConnection> = new Map();

  /** Boolean flag used to indicate if the "Select/Unselect All People" process is running.  Used to disable the Asset UI checkboxes. */
  isSelectingAllPeople = false;

  isAssetSearchOptionChanging = false;

  isPeopleSearchOptionChanging = false;

  /**
   * Components related to the "Zone" Search Functionality
   */
  // HTML Input Element used for the Zone search value
  @ViewChild('zoneSearchInput') zoneSearchInput!: ElementRef;
  // Boolean Flag used to indicate if we are currently querying the backend for "Search Zone(s)" requests. Used to show a "Loading" indicator to the User.
  isSearchingZones = false;

  /**
   * Components related to the "Asset" Search Functionality
   */
  // HTML Input Element used for the Asset search value
  @ViewChild('assetSearchInput') assetSearchInput!: ElementRef;
  // Boolean Flag used to indicate if we are currently querying the backend for "Search Zone(s)" requests. Used to show a "Loading" indicator to the User.
  isSearchingAssets = false;
  // Value entered for searching Assets
  assetSearchValue = "";
  // Options available for searching Assets
  assetSearchOptions: SearchOption[] = [];
  // Selected Search Option for Assets
  selectedAssetSearchOption: SearchOption;

  /**
   * Components related to the "Asset" Search Functionality
   */
  // HTML Input Element used for the Asset search value
  @ViewChild('personSearchInput') personSearchInput!: ElementRef;
  // Boolean Flag used to indicate if we are currently querying the backend for "Search Zone(s)" requests. Used to show a "Loading" indicator to the User.
  isSearchingPeople = false;
  // Value entered for searching People
  personSearchValue = "";
  // Options available for searching People
  peopleSearchOptions: SearchOption[] = [];
  // Selected Search Otpion for People
  selectedPersonSearchOption: SearchOption;

  /**
   * Components used in the "Monitor Zone" Web Socket subscription functionality.
   * This 'Monitor Zone' Web Socket subscribes to the following events on the selected
   * Zone to keep the LHS Lists up to date:
   *   - Asset Entered Zone Event
   *   - Asset Exited Zone Event
   *   - Person Entered Zone Event
   *   - Person Exited Zone Event
   */
  monitorZoneWsSubject: Subject<MessageEvent>;
  monitorZoneWsSubscription: Subscription;

  /** Flag which controls whether Assets / People should be autoselected / autodisplayed on the Map when a Zone is selected, or when they enter the Zone. */
  autoselectInstances: boolean = false;

  /** Results Per age to be displayed */
    resultsPerPageValues = MonitorZone_ResultsPerPage;
    /** Total # of people (Used to figure out # of pages) */
    totalNumberOfPeople = 0;
      /** Total # of Asssets. (Used to figure out # of pages) */
    totalNumberOfAssets = 0;
     /** Total # of Zones. (Used to figure out # of pages) */
    totalNumberOfZones = 0;
    /** Current Page being displayed */
    currentPage= 1;
    currentPageZones = 1;

  /** 
   * Constructor
   */
  constructor(
    private zoneService: ZoneService,
    private objectService: ObjectService,
    private imageService: ImageService,
    private route: ActivatedRoute,
    private monitorZoneWebSocketService: MonitorZoneWebSocketService,
    private objectInstanceWebSocketService: ObjectInstanceWebSocketService
  ) { }

  /** 
   * OnInit() - Called after the UI is initialized, but before it is displayed. 
   */
  ngOnInit(): void {
    // Grab the Data that was resolved in the background as the page was loading...
    this.route.data.subscribe(
      (data: {
        initialZones: PagedResults<ZoneInstance>,
        assetSearchProperties: ObjectProperty[],
        peopleSearchProperties: ObjectProperty[]
      }) => {
        // Load the initial Zones to display to the User
        const initialZones: PagedResults<ZoneInstance> = data.initialZones;
        if (initialZones?.data != null) {
          this.displayedZones = initialZones.data;
          this.totalNumberOfZones = initialZones.size;
        }

        // Load the Search Options for Assets
        {
          const searchOptions: SearchOption[] = [];
          // Add the default "Name" Option
          searchOptions.push(new SearchOption());
          // Add the options for each search property found
          data.assetSearchProperties?.forEach(assetSearchProperty => {
            searchOptions.push(new SearchOption(assetSearchProperty));
          });
          this.assetSearchOptions = searchOptions;

          // Set the default Search Option to the NAME option (always the first one)
          this.selectedAssetSearchOption = searchOptions[0];
        }

        // Load the Search Options for People
        {
          const searchOptions: SearchOption[] = [];
          // Add the default "Name" Option
          searchOptions.push(new SearchOption());
          // Add the options for each search property found
          data.peopleSearchProperties?.forEach(peopleSearchProperty => {
            searchOptions.push(new SearchOption(peopleSearchProperty));
          });
          this.peopleSearchOptions = searchOptions;

          // Set the default Search Option to the NAME option (always the first one)
          this.selectedPersonSearchOption = searchOptions[0];
        }
      }
    );
  }

  /** 
   * AfterViewInit() - Called after OnInit and after the UI is displayed. 
   * Use this to interact with the UI elements
   */
  ngAfterViewInit(): void {
    /*
     * Set up the "Zone Search" functionality
     */ 
    fromEvent(this.zoneSearchInput.nativeElement, 'keyup')
      .pipe(
        // On any Key Up Event, set that we are searching Zones and return the value
        map((event: any) => { 
          this.isSearchingZones = true;
          return event.target.value;
        }),

        // Debounce to limit REST API requests.  Give the User half a second to stop typing before starting to search. 
        debounceTime(500)
      )
      .subscribe((text: string) => {
        //Perform the "Zone Search" Request and update the displayed Zones with the results
        this.zoneService.getAllZoneInstancesForPaging(0, this.resultsPerPageValues, "ASC", text)
          .then(searchResults => {
            this.displayedZones = searchResults.data;
            this.totalNumberOfZones = searchResults.size;
            this.isSearchingZones = false;
          })
          .catch(error => {
            console.error("Problem searching zones.", error);
            this.isSearchingZones = false;
          })
      });

    /*
     * Set up the "Asset Search" functionality
     */
    fromEvent(this.assetSearchInput.nativeElement, 'keyup')
    .pipe(
      // On any Key Up Event, set that we are searching Assets and return the value
      map((event: any) => {
        this.isSearchingAssets = true;
        return event.target.value;
      }),

      // Debounce to limit requests.  Give the User half a second to stop typing before starting to search. 
      debounceTime(500)
    )
    .subscribe((text: string) => {
      this.refreshDisplayedAssetsInList();
      this.isSearchingAssets = false;
    });

    /*
     * Set up the "Person Search" functionality
     */
    fromEvent(this.personSearchInput.nativeElement, 'keyup')
    .pipe(
      // On any Key Up Event, set that we are searching People and return the value
      map((event: any) => {
        this.isSearchingPeople = true;
        return event.target.value;
      }),

      // Debounce to limit requests.  Give the User half a second to stop typing before starting to search. 
      debounceTime(500)
    )
    .subscribe((text: string) => {
      this.refreshDisplayedPeopleInList();
      this.isSearchingPeople = false;
    });
  }

  /** 
   * Called when the Component is being destroyed and used to cleanup resources.
   * Currently ensures the "Monitor Zone" Web Socket is closed.
   */
  ngOnDestroy(): void {
    this.closeMonitorZoneWebSocketIfNeeded();
  }

  /** 
   * UI Interceptor.  Called when a "Zone" is clicked on the UI by
   * the User, indicating it should be selected.
   */
  onZoneClicked(zoneClicked: ZoneInstance): void {
    console.log(`Zone Clicked: '${zoneClicked?.id}/${zoneClicked?.name}' - Is Selecting Zone? ${this.isSelectingZone}`);
    if (this.isSelectingZone) {
      console.warn("Ignoring Zone Click as a zone was already being selected.");
      return;
    }

    // Update the "isSelectingZone" Flag since a Zone is about to be selected... (Will be reset by the "setSelectedZone()" async method)
    this.isSelectingZone = true;

    // Set the selected Zone to the Zone that was clicked, unless it was the Zone that was clicked, in which case unselect it.
    this.setSelectedZone(zoneClicked?.id == this.selectedZone?.id? null : zoneClicked);
  }

  /**
   * Sets the selected Zone to be displayed / monitored.  If null is passed, it indicates
   * to unselect the zone.  This loads the displayed Assets / People based on the Zone. 
   */
  private async setSelectedZone(zoneSelected: ZoneInstance): Promise<void> {
    console.log(`Setting Selected Zone: '${zoneSelected?.id}/${zoneSelected?.name}'...`);

    // Boolean flag used to indicate if a Zone is being selected or unselected
    const isZoneSelected = zoneSelected?.id != null;

    // Reset the Search Options
    this.assetSearchValue = "";
    this.selectedAssetSearchOption = this.assetSearchOptions[0];
    this.personSearchValue = "";
    this.selectedPersonSearchOption = this.peopleSearchOptions[0];

    // Handle closing the "Monitor Zone Web Socket" if un-selecting first to prevent any more messages from needing to be processed
    this.closeMonitorZoneWebSocketIfNeeded();

    // Stop tracking all Assets / People that are currently being tracked...
    await this.stopTrackingAllAssets();
    await this.stopTrackingAllPeople();

    // If we were previously displaying a Zone, then it needs to be cleaned up / removed
    if (this.selectedZone != null) {
      await this.locateMap.removeAllAssetsFromDisplay();
      await this.locateMap.removeAllPeopleFromDisplay();
      await this.locateMap.removeZoneFromDisplay(this.selectedZone);
    }

    // Load the Zone / Assets / People that should be displayed to the User
    let zone: ZoneInstance = null;
    let assets: ObjectInstance[] = null;
    let people: ObjectInstance[] = null;

    if (!isZoneSelected) {
      // If we are unselecting a Zone, then there are no assets / people, just use an empty array
      assets = [];
      people = [];
    } else {
      // Query the backend for the Assets / People in parallel 
      const assetsPromise = await this.objectService.getAllObjectInstancesForCategoryInZonePaged(ObjectCategory.ASSET, zoneSelected.id, 0, MonitorZone_ResultsPerPage, 'ASC', '', '');
      const peoplePromise =await  this.objectService.getAllObjectInstancesForCategoryInZonePaged(ObjectCategory.PEOPLE, zoneSelected.id, 0, MonitorZone_ResultsPerPage, 'ASC', '', '');
      const zonePromise = await this.zoneService.getZoneInstanceById(zoneSelected.id);

      let results: { assets: ObjectInstance[], people: ObjectInstance[] , zone: ZoneInstance } = null;
      try {
        results = { assets: assetsPromise.data, people: peoplePromise.data, zone: zonePromise };
      } catch (error) {
        console.error("Problem obtaining Zone / Assets / People in selected zone.", error);
      }

      zone = results?.zone;
      assets = results?.assets;
      people = results?.people;
      this.totalNumberOfAssets = assetsPromise?.size;
      this.totalNumberOfPeople = peoplePromise?.size;
      this.currentPage = 1;
    }

    // Update Assets / People and Selected Zone
    this.assetsInZone = assets? assets : [];
    this.peopleInZone = people? people : [];
    this.displayedZones = this.displayedZones.map(displayedZone => displayedZone?.id == zone?.id ? zone : displayedZone);
    this.selectedZone = zone;

    // Sort and refresh the displayed Assets in the List
    this.sortAssets();
    this.refreshDisplayedAssetsInList();

    // Sort and refresh the displayed People in the List
    this.sortPeople();
    this.refreshDisplayedPeopleInList();

    // If a Zone was selected, create the "Zone Monitor" Web Socket to dynamically update the Assets / People lists
    if (isZoneSelected) {
      await this.createMonitorZoneWebSocket(this.selectedZone);
    }

    // Draw the Locaton on the Map
    await this.locateMap.setDisplayedLocation(this.selectedZone?.locationInstance);

    // Draw / Zoom to the selected Zone on the Map (if one was selected)
    if (isZoneSelected) {
      await this.locateMap.addZoneToDisplay(this.selectedZone);
      await this.locateMap.executeZoomToZone(this.selectedZone);

      if (this.autoselectInstances) {
        const promises: Promise<void>[] = [];
        promises.push(this.selectAllAssetsAsync(true));
        promises.push(this.selectAllPeopleAsync(true));
        if (promises.length > 0) {
          await Promise.all(promises);
        }
      }
    }

    // Update the "isSelectingZone" Flag since we are done selecting the Zone
    this.isSelectingZone = false;
  }

  /**
   * Closes the "Monitor Zone" Web Socket connection used for the Asset / Person Entered / Exited Zone Events.
   * This should be called when a Zone is unselected so that the LHS Lists aren't dynamically updated. 
   */
  private closeMonitorZoneWebSocketIfNeeded(): void {
    if (this.monitorZoneWsSubject) {
      this.monitorZoneWsSubject.unsubscribe();
      this.monitorZoneWsSubject = null;
    }
    if (this.monitorZoneWsSubscription) {
      this.monitorZoneWsSubscription.unsubscribe();
      this.monitorZoneWsSubscription = null;
    }
  }

  /**
   * Creates the "Monitor Zone" Web Socket connection used for the Asset / Person Entered / Exited Zone Events.
   * This should be called when a Zone is selected so that the LHS Lists are dynamically updated. 
   */
  private async createMonitorZoneWebSocket(zone: ZoneInstance): Promise<void> {
    // Ensure the Web Socket is closed (shouldn't be needed but done for redundancy)
    this.closeMonitorZoneWebSocketIfNeeded();

    // Create the Web Socket Connection / Subject
    try {
      this.monitorZoneWsSubject = this.monitorZoneWebSocketService.create(zone.id);
    } catch (error) {
      console.error("Problem creating Monitor Zone Web Socket.", error);
      this.monitorZoneWsSubject = null;
      return;
    }

    // Create the Web Socket Connection / Subscription
    this.monitorZoneWsSubscription = this.monitorZoneWsSubject.subscribe(
      message => { this.handleEventFromMonitorZoneWs(message); },
      error => {
        console.error("Error encountered on 'Monitor Zone' Web Socket!", error);
        this.closeMonitorZoneWebSocketIfNeeded();
      });
  }

  /**
   * Called whenever an "Event" is seen on the "Monitor Zone" Web Socket used to 
   * update the LHS Assets / People lists for the selected Zone.
   */
  private handleEventFromMonitorZoneWs(message: MessageEvent<any>): void {
    // Parse the MessageEvent data as the expected EventInstance
    const event = JSON.parse(message.data) as EventInstance;

    if (event?.event?.uuid == null) {
      console.warn("Ignoring unknown / unhandled event from 'Monitor Zone' Web Socket.");
      return;
    }

    switch (event.event.uuid) {
      case EventUuid_AssetEnteredZone: {
        this.handleAssetEnteredZoneEventFromMonitorZoneWs(event);
        break;
      }
      case EventUuid_AssetExitedZone: {
        this.handleAssetExitedZoneEventFromMonitorZoneWs(event);
        break;
      }
      case EventUuid_PersonEnteredZone: {
        this.handlePersonEnteredZoneEventFromMonitorZoneWs(event);
        break;
      }
      case EventUuid_PersonExitedZone: {
        this.handlePersonExitedZoneEventFromMonitorZoneWs(event);
        break;
      }
      default: {
        console.warn(`Unhandled Event found on Monitor Zone Ws!  Event Type: '${event?.event?.id}/${event?.event?.name}/${event?.event?.uuid}'!`);
        break;
      }
    }
  }

  /**
   * Handles a "Asset Entered Zone" Event.  
   * Obtains the Asset entering the Zone from the "asset" Event Data
   * and adds them to the Assets in the Zone.
   */
  private async handleAssetEnteredZoneEventFromMonitorZoneWs(event: EventInstance): Promise<void> {
    const assetDatum = event.data.find(d => d.property.property == EVENT_PROP_Asset);
    if (assetDatum?.object_value?.object_reference) {
      const asset = assetDatum.object_value.object_reference as ObjectInstance;
      this.assetsInZone.push(asset);
      this.sortAssets();
      this.refreshDisplayedAssetsInList();
      if (this.autoselectInstances) {
        await this.startTrackingAsset(asset);
      }
    }
  }

  /**
   * Handles a "Asset Exited Zone" Event.  
   * Obtains the Asset leaving the Zone from the "asset" Event Data
   * and removes the corresponding asset from the Displayed Assets List.
   */
  private async handleAssetExitedZoneEventFromMonitorZoneWs(event: EventInstance): Promise<void> {
    const assetDatum = event.data.find(d => d?.property?.property == EVENT_PROP_Asset);
    if (assetDatum?.object_value?.object_reference) {
      const assetFromData = assetDatum.object_value.object_reference as ObjectInstance;
      const assetInList = this.assetsInZone.find(da => da.id == assetFromData?.id);
      if (assetInList != null) {
        await this.stopTrackingAsset(assetInList);
        await this.locateMap.removeAssetFromDisplay(assetInList);
        this.assetsInZone = this.assetsInZone.filter(da => da.id != assetInList.id);
        this.assetsInList = this.assetsInList.filter(da => da.id != assetInList.id);
      }
    }
  }

  /**
   * Handles a "Person Entered Zone" Event.  
   * Obtains the Person entering the Zone from the "person" Event Data
   * and adds them to the Displayed People List.
   */
  private async handlePersonEnteredZoneEventFromMonitorZoneWs(event: EventInstance): Promise<void> {
    const personDatum = event.data.find(d => d.property.property == EVENT_PROP_Person);
    if (personDatum?.object_value?.object_reference) {
      const person = personDatum.object_value.object_reference as ObjectInstance;
      this.peopleInZone.push(person);
      this.sortPeople();
      this.refreshDisplayedPeopleInList();
      if (this.autoselectInstances) {
        await this.startTrackingPerson(person);
      }
    }
  }

  /**
   * Handles a "Person Exited Zone" Event.  
   * Obtains the Person leaving the Zone from the "person" Event Data
   * and removes the corresponding person from the Displayed People List.
   */
  private async handlePersonExitedZoneEventFromMonitorZoneWs(event: EventInstance): Promise<void> {
    const personDatum = event.data.find(d => d.property.property == EVENT_PROP_Person);
    if (personDatum?.object_value?.object_reference) {
      const personFromData = personDatum.object_value.object_reference as ObjectInstance;
      const personInList = this.peopleInZone.find(dp => dp.id == personFromData?.id);
      if (personInList != null) {
        await this.stopTrackingPerson(personInList);
        await this.locateMap.removePersonFromDisplay(personInList);
        this.peopleInZone = this.peopleInZone.filter(dp => dp.id != personInList.id);
        this.peopleInList = this.peopleInList.filter(dp => dp.id != personInList.id);
      }
    }
  }

  /**
   * Sorts the Displayed Assets by their name
   */
  private sortAssets(): void {
    this.assetsInZone.sort((a: ObjectInstance, b: ObjectInstance) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name == b.name) {
        return 0;
      } else {
        return 1;
      }
    });
  }

  /** Refresh the Zone list */
  private async refreshDisplayedZonesInList(): Promise<void> {
    const searchValue = this.assetSearchValue;
    const searchResults =  await this.zoneService.getAllZoneInstancesForPaging(
          (this.currentPageZones - 1) * this.resultsPerPageValues,
          this.resultsPerPageValues,
          "ASC",
          (searchValue || '').trim(),);

    if(searchResults != null) {
      this.displayedZones = searchResults.data;
      this.totalNumberOfZones = searchResults.size;
    }
  }

  /**
   * Refreshes the "assetsInList" ObjectInstace[] based on the currently
   * values in the "assetsInZone" ObjectInstance[] and the current Asset
   * Search Values.  These are the Assets that should be displayed to the 
   * User in the LHS List. 
   */
  private async refreshDisplayedAssetsInList(): Promise<void> {
    const assets: ObjectInstance[] = [];
    const searchValue = this.assetSearchValue;
    const filterColumn = this.selectedAssetSearchOption?.searchType == SearchType.PROPERTY ? this.selectedAssetSearchOption?.property?.property : this.selectedAssetSearchOption?.searchType;


        // Fetch the Asssets from the backend with the search option if any and display it on the UI
    const assetsPromise = await this.objectService.getAllObjectInstancesForCategoryInZonePaged(ObjectCategory.ASSET, this.selectedZone?.id,
          (this.currentPage - 1) * this.resultsPerPageValues,
          this.resultsPerPageValues,
          "ASC",
          (searchValue || '').trim(),
          filterColumn);
          console.log("SearchType JOSN", JSON.stringify(filterColumn));
    
    this.assetsInZone = assetsPromise?.data;
    this.totalNumberOfAssets = assetsPromise?.size;

    this.isAllAssetsBeingTracked();

    // If there's no Search Value entered, then display all the Assets
    if (this.assetsInZone?.length > 0) {
      assets.push(...this.assetsInZone);
    }
    this.assetsInList = assets;
  }

  /**
   * Sorts the Displayed People by their name
   */
  private sortPeople(): void {
    this.peopleInZone.sort((a: ObjectInstance, b: ObjectInstance) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name == b.name) {
        return 0;
      } else {
        return 1;
      }
    });
  }

  /**
   * Refreshes the "peopleInList" ObjectInstace[] based on the currently
   * values in the "peopleInZone" ObjectInstance[] and the current People
   * Search Values.  These are the People that should be displayed to the 
   * User in the LHS List. 
   */
  private async refreshDisplayedPeopleInList(): Promise<void> {
    const people: ObjectInstance[] = [];
    const searchValue = this.personSearchValue;
   const filterColumn = this.selectedPersonSearchOption?.searchType == SearchType.PROPERTY ? this.selectedPersonSearchOption?.property?.property : this.selectedPersonSearchOption?.searchType;
    
    const peoplePromise = await this.objectService.getAllObjectInstancesForCategoryInZonePaged(ObjectCategory.PEOPLE, this.selectedZone?.id, 
          (this.currentPage - 1) * this.resultsPerPageValues,
          this.resultsPerPageValues,
          "ASC",
          (searchValue || '').trim(),
          filterColumn);
          console.log("SearchType JOSN", JSON.stringify(filterColumn));

    this.peopleInZone = peoplePromise?.data;
    this.totalNumberOfPeople = peoplePromise?.size;
     if (this.peopleInZone?.length > 0) {
      people.push(...this.peopleInZone);
    }
    this.peopleInList = people;
  }

  /**
   * Returns a boolean indicating if the given Instance should be displayed in the LHS
   * List based on the given search criteria.
   */
  private shouldInstanceBeDisplayedInList(instance: ObjectInstance, searchOption: SearchOption, searchValue: string): boolean {
    // If the Instance is being tracked, then it should be shown.
    if (this.isInstanceTracked(instance)) {
      return true;
    }
    
    // Instance isn't being tracked, then it needs to pass the Search Criteria
    switch (searchOption?.searchType) {
      case SearchType.NAME: {
        /*
         * Search by Name
         * Returns true if the search value is anywhere in the Instance's Name (case in-sensitive). 
         */
        return instance.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
      }
      case SearchType.PROPERTY: {
        /*
         * Search by Property
         * Returns true if the Instance Data for the Property being searched by 
         * and if the data matches criteria depending on the search value:
         *  1) Search Value = "?" 
         *     As long as Data exists and is specified for the Property, the 
         *     Instance should be shown so true will be returned.
         *  2) Anything Else
         *     Depends on the Property Type: 
         *       A) INTEGER
         *          Returns true if the search value is the same numerically as the 
         *          matching data value
         *       B) STRING / Anything Else
         *          Returns true if the search value is anywhere in the matching
         *          data value (case in-sensitive). 
         */
        // Find any matching Instance Data for the Property specified in the search
        const dataForProperty = instance?.data?.filter(datum => datum?.property?.id == searchOption?.property?.id);
        
        // If no data was found for the Property, then the Instance should not be shown.
        if (dataForProperty.length == 0) {
          return false;
        }

        /*
         * 1) If Search Value is "?", then as long as data is specified that 
         * contains a value, return true.
         */
        if (searchValue == '?') {
          return dataForProperty.find(datum => datum?.value?.trim()?.length > 0) != null;
        }

        /*
         * 2) Anything Else, search based on the Property Type of the Property specified
         * in the search criteria. 
         */
        switch (searchOption?.property?.type) {
          case PropertyType.INTEGER: {
            /* 
             * A) INTEGER - Return true if the search value is the same numerically as the 
             *    matching data value
             */
            return dataForProperty.find(datum => Number(datum?.value) == Number(searchValue)) != null;
          }
          case PropertyType.STRING: 
          default: {
            /*
             * B) STRING / Anything Else - Return true if the search value is anywhere in 
             *    the matching data value (case in-sensitive). 
             */
            return dataForProperty.find(datum => datum?.value?.toLowerCase()?.indexOf(searchValue?.toLowerCase()) > -1) != null;
          }
        }
      }
    }
  }

  /**
   * Updates the Displayed Instance (Asset / Person) with the information in the 
   * given Instance. Currently this updates the following:
   *   1) Data Values
   *   2) Object Reference (for Functions)
   */
  private updateDisplayedInstance(instance: ObjectInstance): void {
    // Sort the Data by the Property Order value...
    instance?.data?.sort((a, b) => a.property.order - b.property.order);

    // Update the information in the displayed version
    switch (instance?.object?.category) {
      case ObjectCategory.ASSET: {
        const displayedAsset = this.assetsInZone.find(da => da?.id == instance?.id);
        if (displayedAsset != null) {
          displayedAsset.data = instance.data;
          displayedAsset.object = instance.object;
        }
        break;
      }
      case ObjectCategory.PEOPLE: {
        const displayedPerson = this.peopleInZone.find(dp => dp?.id == instance?.id);
        if (displayedPerson != null) {
          displayedPerson.data = instance.data;
          displayedPerson.object = instance.object;
        }
        break;
      }
      default: {
        console.warn(`Cannot update Displayed Instance of Unknown Type: '${instance?.object?.category}'.  No List version shall have the changes reflected.`);
        break;
      }
    }
  }

  /**
   * Returns the String URL for the <img>'s "src" property used
   * to display the image for the given Object Instance
   */
  public getImageSource(objectInstance: ObjectInstance): string {
    return this.imageService.getImageSourceForObjectInstance(objectInstance);
  }

  /**
   * Returns a boolean indicating if the Asset Image is present.
   * Used in the HTML to display the Image or not. 
   */
  public isImagePresent(asset: ObjectInstance): boolean {
    if (asset.image != null) {
      return true;
    }
    return false;
  }

  /**
   * Returns the String URL for the <img>'s "src" property used
   * to display the icon for the given Object Instance
   */
  public getIconSource(objectInstance: ObjectInstance): string {
    return this.imageService.getIconSourceForObjectInstance(objectInstance);
  }

  /*
   * Returns a boolean indicating if the "Full Details" section is needed for the given Object Instance.
   * It is only needed if the Object Instance has Property Data for a Property that isn't flagged as "Main Display"
   */
  isFullDetailsNeeded(objectInstance: ObjectInstance): boolean {
    return objectInstance?.data?.find(datum => datum?.property?.mainDisplay == false) != null;
  }

  /**
   * Returns a boolean indicating if the given AssetUI has any Functions that are exposed via
   * it's Asset Type that can be called / executed.  Used to display the Functions as Actions
   * in the Assets Tab when the Asset is expanded.
   */
  isContainsFunctions(objectInstance: ObjectInstance): boolean {
    return objectInstance?.object?.functions?.length > 0;
  }

  /**
   * Called when a Function is clicked on in the Actions dropdown on an Object Instance.  The corresponding
   * Object Instance and the Function Key for the Function that was clicked are passed as parameters. 
   */
  openFunctionDialog(instance: ObjectInstance, functionKey: string): void {
    this.instanceFunctionDialogComponent.setInstanceAndFunction(instance, functionKey);
    this.instanceFunctionDialogComponent.show();
  }

  /**
   * Handler for the ObjectInstanceFunctionDialogComponent which gets called on a successful Function
   * execution with an updated version of the Asset the function was performed on.
   * 
   * This replaces the ObjectInstance in the UI List with the up-to-date version so that changes
   * to the Property Data can be reflected in the UI.
   */
  onFunctionCompletedSuccessfully(instance: ObjectInstance): void {
    // Update the instance in the displayed list so that the data changes can be reflected there
    this.updateDisplayedInstance(instance);

    // Hide the Instance Function Dialog Component
    this.instanceFunctionDialogComponent.hide();
  }

  /**
   * Called when the "Edit Data" Action is clicked in the Action(s) dropdown on an Object Instance. 
   * Displays the ObjectInstanceDataDialog to allow the User to edit the Data for the given ObjectInstance. 
   */
  openDataDialog(instance: ObjectInstance): void {
    this.instanceDataDialogComponent.setInstance(instance);
    this.instanceDataDialogComponent.show();
  }

  /**
   * Handler for the InstanceDataDialogComponent which gets called on a successful "Update Data"
   * execution with an updated version of the Asset whose Data was updated.
   * 
   * This updates the version in the UI List with the information from the returned after update version
   * so that the updated Property Data can be reflected in the UI.
   */
  onDataUpdatedSuccessfully(instance: ObjectInstance): void {
    // Update the instance in the displayed list so that the data changes can be reflected there
    this.updateDisplayedInstance(instance);

    // Hide the Instance Data Dialog Component
    this.instanceDataDialogComponent.hide();
  }

  /**
   * Closes / Unsubscribes from the Web Socket connection defined in the given WsConnection.
   */
  closeWsConnection(wsConnection: WsConnection): void {
    if (wsConnection.wsSubject != null) {
      wsConnection.wsSubject.unsubscribe();
      wsConnection.wsSubject = null;
    }
    if (wsConnection.wsSubscription != null) {
      wsConnection.wsSubscription.unsubscribe();
      wsConnection.wsSubscription = null;
    }
  }

  /**
   * Called when an Asset in the LHS is clicked.
   * Depending on the "checked" property of the Event coming in, this either
   * starts tracking the Asset, or stops tracking the Asset.
   */
  onAssetCheckboxToggled(asset: ObjectInstance, event): void {
    if (asset?.id == null) {
      console.warn("Ignoring Asset being toggled since ID was not found.");
      return;
    }

    // Add the Asset to the Map of Asset's being toggled...
    this.assetsBeingToggled.set(asset.id, asset);

    // Depending on if this checked or unchecked, either start or stop the tracking of the Asset
    if (event?.target?.checked) {
      this.startTrackingAsset(asset);
    } else {
      this.stopTrackingAsset(asset);
    }
  }

  /**
   * Returns a boolean indicating if the Asset is having it's checkbox toggled,
   * meaning it is either being added to the display / tracked, or being removed
   * from the display / tracked. 
   * 
   * Used to prevent the toggling of the checkbox if things are being changed.
   */
  isAssetCheckboxBeingToggled(asset: ObjectInstance): boolean {
    return asset?.id == null? false : this.assetsBeingToggled.has(asset.id);
  }

  /**
   * Starts tracking the given Asset.
   */
  private async startTrackingAsset(asset: ObjectInstance): Promise<void> {
    // Retrieve a more "current" version of the Asset being tracked
    let freshAsset: ObjectInstance = null;
    try {
      freshAsset = await this.objectService.getObjectInstanceForId(asset.id);
    } catch (error) {
      console.warn("Problem obtaining fresh data on Asset being tracked.  Possibly stale data will be used.", error);
    }
    if (freshAsset != null) {
      asset = freshAsset;
      this.updateDisplayedInstance(asset);
    }

    // Add the Asset to the displayed Map (wait for it to actually be displayed before continuing...)
    await this.locateMap.addAssetToDisplay(asset);

    if (this.assetsBeingTracked.has(asset.id)) {
      console.warn("NOT creating WS connection for Asset as it is already being tracked...");
    } else {
      const wsConnection = new WsConnection();
      wsConnection.wsSubject = this.objectInstanceWebSocketService.create(asset.id);
      wsConnection.wsSubscription = wsConnection.wsSubject.subscribe(
        message => { this.handleAssetWebSocketMessage(message, asset); },
        error => {
          console.error(`Error detected opening WS Connection for Asset: '${asset.id}/${asset.name}'`, error);
          this.closeWsConnection(wsConnection);
        });
      this.assetsBeingTracked.set(asset.id, wsConnection);
    }

    // Remove the Asset from the Map of Assets which are being toggled.
    this.assetsBeingToggled.delete(asset.id);    
  }

  /**
   * Stops tracking the given Asset.
   */
  private async stopTrackingAsset(asset: ObjectInstance): Promise<void> {
    // Remove the Asset to the displayed Map (wait for it to actually be removed before continuing...)
    await this.locateMap.removeAssetFromDisplay(asset);

    if (!this.assetsBeingTracked.has(asset.id)) {
      console.warn("NOT destroying WS connection for Asset as it doesn't exist.");
    } else {
      const wsConnection = this.assetsBeingTracked.get(asset.id);
      this.closeWsConnection(wsConnection);
      this.assetsBeingTracked.delete(asset.id);
    }

    // Remove the Asset from the Map of Assets which are being toggled.
    this.assetsBeingToggled.delete(asset.id);

    // If the LHS List is displaying search results, refresh them as the Asset no longer tracked may need to be removed.
    if (!this.isSearchingAssets && this.assetSearchValue != null) {
      this.refreshDisplayedAssetsInList();
    }
  }

  /**
   * Stops tracking all Assets that are currently being tracked. 
   */
  private async stopTrackingAllAssets(): Promise<void> {
    this.assetsBeingTracked.forEach((value: WsConnection, assetId: number) => {
      this.closeWsConnection(value);
    });
    this.assetsBeingTracked.clear();
  }

  /**
   * Called whenever an "Event" is seen on one of the "Object Instance Web Socket" connections being
   * used to monitor / track Assets on the screen.  
   */
  private async handleAssetWebSocketMessage(message: MessageEvent<any>, asset: ObjectInstance): Promise<void> {
    // Parse the MessageEvent data as the expected EventInstance
    const event = JSON.parse(message.data) as EventInstance;

    // Make sure the UUID of the Event Type exists so it can be compared against
    if (event?.event?.uuid == null) {
      console.warn("Ignoring unknown / unhandled event from 'Object Instance' (ASSET) Web Socket.");
      return;
    }

    // Depending on the Event Type, handle the Event...
    switch (event.event.uuid) {
      /*
       * Asset Movement Event
       * Updates the position of the Asset if the Event is for the selected Zone.
       */
      case EventUuid_AssetMovement: {
        const isForZone = event?.zoneInstance?.id == this.selectedZone?.id;
        if (isForZone) {
          asset.position = event.point;
          await this.locateMap.updateAssetPosition(asset);
        }
        break;
      }
    }
  }

  /**
   * Called when an Person in the LHS is clicked.
   * Depending on the "checked" property of the Event coming in, this either
   * starts tracking the Person, or stops tracking the Person.
   */
  onPersonCheckboxToggled(person: ObjectInstance, event): void {
    if (person?.id == null) {
      console.warn("Ignoring Person being toggled since ID was not found.");
      return;
    }

    // Add the Person to the Map of People being toggled...
    this.peopleBeingToggled.set(person.id, person);

    // Depending on if this checked or unchecked, either start or stop the tracking of the Person
    if (event?.target?.checked) {
      this.startTrackingPerson(person);
    } else {
      this.stopTrackingPerson(person);
    }
  }

  /**
   * Starts tracking the given Person.
   */
  private async startTrackingPerson(person: ObjectInstance): Promise<void> {
    // Retrieve a more "current" version of the Person being tracked
    let freshPerson: ObjectInstance = null;
    try {
      freshPerson = await this.objectService.getObjectInstanceForId(person.id);
    } catch (error) {
      console.warn("Problem obtaining fresh data on Person being tracked.  Possibly stale data will be used.", error);
    }
    if (freshPerson != null) {
      person = freshPerson;
      this.updateDisplayedInstance(person);
    }

    // Add the Person to the displayed Map (wait for it to actually be displayed before continuing...)
    await this.locateMap.addPersonToDisplay(person);

    if (this.peopleBeingTracked.has(person.id)) {
      console.warn("NOT creating WS connection for Person as it is already being tracked...");
    } else {
      const wsConnection = new WsConnection();
      wsConnection.wsSubject = this.objectInstanceWebSocketService.create(person.id);
      wsConnection.wsSubscription = wsConnection.wsSubject.subscribe(
        message => { this.handlePersonWebSocketMessage(message, person); },
        error => {
          console.error(`Error detected opening WS Connection for Person: '${person.id}/${person.name}'`, error);
          this.closeWsConnection(wsConnection);
        });
      this.peopleBeingTracked.set(person.id, wsConnection);
    }

    // Remove the Person from the Map of People which are being toggled.
    this.peopleBeingToggled.delete(person.id);    

    // If the LHS List is displaying search results, refresh them as the Person no longer tracked may need to be removed.
    if (!this.isSearchingPeople && this.personSearchValue != null) {
      this.refreshDisplayedPeopleInList();
    }
  }

  /**
   * Stops tracking the given Person.
   */
  private async stopTrackingPerson(person: ObjectInstance): Promise<void> {
    // Remove the Person to the displayed Map (wait for it to actually be removed before continuing...)
    await this.locateMap.removePersonFromDisplay(person);

    if (!this.peopleBeingTracked.has(person.id)) {
      console.warn("NOT destroying WS connection for Person as it doesn't exist.");
    } else {
      const wsConnection = this.peopleBeingTracked.get(person.id);
      this.closeWsConnection(wsConnection);
      this.peopleBeingTracked.delete(person.id);
    }

    // Remove the Person from the Map of People which are being toggled.
    this.peopleBeingToggled.delete(person.id); 
  }

  /**
   * Returns a boolean indicating if the Person is having it's checkbox toggled,
   * meaning it is either being added to the display / tracked, or being removed
   * from the display / tracked. 
   * 
   * Used to prevent the toggling of the checkbox if things are being changed.
   */
   isPersonCheckboxBeingToggled(person: ObjectInstance): boolean {
    return person?.id == null? false : this.peopleBeingToggled.has(person.id);
  }

  /**
   * Stops tracking all People that are currently being tracked. 
   */
  private async stopTrackingAllPeople(): Promise<void> {
    this.peopleBeingTracked.forEach((value: WsConnection, personId: number) => {
      this.closeWsConnection(value);
    });
    this.peopleBeingTracked.clear();
  }

  /**
   * Called whenever an "Event" is seen on one of the "Object Instance Web Socket" connections being
   * used to monitor / track People on the screen.  
   */
  private async handlePersonWebSocketMessage(message: MessageEvent<any>, person: ObjectInstance): Promise<void> {
    // Parse the MessageEvent data as the expected EventInstance
    const event = JSON.parse(message.data) as EventInstance;

    // Make sure the UUID of the Event Type exists so it can be compared against
    if (event?.event?.uuid == null) {
      console.warn("Ignoring unknown / unhandled event from 'Object Instance' (ASSET) Web Socket.");
      return;
    }

    // Depending on the Event Type, handle the Event...
    switch (event.event.uuid) {
      /*
       * Person Movement Event
       * Updates the position of the Person if the Event is for the selected Zone.
       */
      case EventUuid_PersonMovement: {
        const isForZone = event?.zoneInstance?.id == this.selectedZone?.id;
        if (isForZone) {
          person.position = event.point;
          await this.locateMap.updatePersonPosition(person);
        }
        break;
      }
    }
  }

  /**
   * Returns the "id" of the "<div>" HTML element being used as the 
   * given Object Instance's LHS List Body displaying extra information
   * about the Object Instance.  Used to dynamically show / hide the 
   * List Body on click.
   */
  getIdForListBody(instance: ObjectInstance): string {
    return `mzListBody_${instance.id}`;
  }

  /**
   * Displays the LHS List Body for the given Object Instance.
   * This fetches fresh information from the Server about the 
   * selected Object Instance and updates the local version 
   * before displaying the List Body.
   */
  async showListBody(instance: ObjectInstance): Promise<void> {  
    // Fetch updated information about the Object Instance from the Server...
    let newInstance: ObjectInstance = null;
    try {
      newInstance = await this.objectService.getObjectInstanceForId(instance.id);
    } catch (error) {
      console.error("Problem obtaining Object Instance when showing List Body!", error);
      return;
    }
    if (newInstance == null) {
      console.warn("Cannot show List Body for Object Instance as no value was retrieved from Server.");
      return;
    }

    // Update the instance in the displayed list so that the data changes can be reflected there
    this.updateDisplayedInstance(newInstance);

    // Display the List Body
    $(`#${this.getIdForListBody(instance)}`).removeClass('hidden');
  }

  /**
   * Hides the LHS List Body for the given Object Instance.
   */
  async hideListBody(instance: ObjectInstance): Promise<void> {
    $(`#${this.getIdForListBody(instance)}`).addClass('hidden');
  }

  /**
   * Event Handler called when the LHS List Header is clicked on 
   * for the given Object Instance.  Used to toggle the LHS 
   * List Body.
   */
  onListHeaderClicked(instance: ObjectInstance): void {
    // Depending on whether the List Body is hidden or not, either display or hide the List Body
    if ($(`#${this.getIdForListBody(instance)}`).hasClass('hidden')) {
      this.showListBody(instance);
    } else {
      this.hideListBody(instance);
    }
  }

  /**
   * Returns the ID of the HTML Container being used to display
   * the Full Details for the given Object Instance (Asset / Person).
   * Used for showing / hiding the Full Details Display.
   */
  getIdForFullDetails(instance: ObjectInstance): string {
    return `mzFullDetails_${instance.id}`;
  }

  /**
   * Returns the ID of the HTML Element being used to display the
   * option for displaying the Full Details when clicked. 
   * Used for showing the Full Details on click, and toggling the display
   * of this HTML Element when appropriate. 
   */
  getIdForShowFullDetails(instance: ObjectInstance): string {
    return `mzShowFullDetails_${instance.id}`;
  }

  /**
   * Returns the ID of the HTML Element being used to display the
   * option for hiding the Full Details when clicked. 
   * Used for hiding the Full Details on click, and toggling the display
   * of this HTML Element when appropriate. 
   */
  getIdForHideFullDetails(instance: ObjectInstance): string {
    return `mzHideFullDetails_${instance.id}`;
  }

  /**
   * Shows the Full Details display for the given Object Instance (Asset / Person).
   */
  showFullDetails(instance: ObjectInstance): void {
    $(`#${this.getIdForShowFullDetails(instance)}`).hide();
    $(`#${this.getIdForHideFullDetails(instance)}`).show();
    $(`#${this.getIdForFullDetails(instance)}`).show();
  }

  /**
   * Hides the Full Details display for the given Object Instance (Asset / Person).
   */
  hideFullDetails(instance: ObjectInstance): void {
    $(`#${this.getIdForHideFullDetails(instance)}`).hide();
    $(`#${this.getIdForShowFullDetails(instance)}`).show();
    $(`#${this.getIdForFullDetails(instance)}`).hide();
  }

  /**
   * Returns a boolean indicating if the givne Object Instance is being
   * tracked or not.
   */
  isInstanceTracked(instance: ObjectInstance): boolean {
    switch (instance?.object?.category) {
      case ObjectCategory.ASSET: {
        return this.assetsBeingTracked.get(instance?.id) != null;
      }
      case ObjectCategory.PEOPLE: {
        return this.peopleBeingTracked.get(instance?.id) != null;
      }
      default: {
        console.log(`Cannot determine if Instance: '${instance?.object?.category}/${instance?.id}/${instance?.name}' is tracked.`);
        return false;
      }
    }
  }

  /**
   * Returns a boolean indicating if the Instance is actually being tracked.
   * The "isInstanceTracked()" method returns if we are drawing the Instance
   * and trying to track it, however if there's an error opening the Web Socket
   * connection it will still return true.  This returns the status of the Web 
   * Socket connection. 
   */
  isInstanceActuallyTracked(instance: ObjectInstance): boolean {
    switch (instance?.object?.category) {
      case ObjectCategory.ASSET: {
        return this.assetsBeingTracked.get(instance?.id)?.wsSubscription != null;
      }
      case ObjectCategory.PEOPLE: {
        return this.peopleBeingTracked.get(instance?.id)?.wsSubscription != null;
      }
      default: {
        console.log(`Cannot determine if Instance: '${instance?.object?.category}/${instance?.id}/${instance?.name}' is actually tracked.`);
        return false;
      }
    }
  }

  /**
   * Returns a boolean indicating if all the Assets are being
   * tracked or not.  Used to determine if the "Select All" Checkbox
   * should be checked or not. 
   */
  isAllAssetsBeingTracked(): boolean {
    if (this.assetsInList.length == 0) {
      return false;
    }
    return this.assetsInList.every(assetInList => this.assetsBeingTracked.has(assetInList?.id));
  }

  /**
   * Starts the "Select All" / "Unselect All" Process based on the 
   * given select parameter.  This kicks off a background process
   * to perform the action, using the "isSelectingAllAssets" flag
   * to indicate if it is running or not and to disable the UI 
   * checkboxes.
   */
  public selectAllAssets(select: boolean): void {
    this.isSelectingAllAssets = true;
    this.selectAllAssetsAsync(select);
  }

  /**
   * This is the "Select All" / "Unselect All" Async Process that is kicked off.
   * This performs that process based on the given select parameter and then sets 
   * the "isSelectingAllAssets" flag back to false.
   */
  private async selectAllAssetsAsync(select: boolean): Promise<void> {
    const promises: Promise<void>[] = [];
    if (select) {
      this.assetsInList.forEach(assetInList => {
        if (!this.assetsBeingTracked.has(assetInList?.id)) {
          promises.push(this.startTrackingAsset(assetInList));
        }
      });
    } else {
      this.assetsInList.forEach(assetInList => {
        if (this.assetsBeingTracked.has(assetInList?.id)) {
          promises.push(this.stopTrackingAsset(assetInList));
        }
      });
    }
    await Promise.all(promises);

    // Update the "isSelectingAllAssets" flag to indicate the process is done and enable the UI checkboxes
    this.isSelectingAllAssets = false;
  }

  /**
   * Returns a boolean indicating if all the People are being
   * tracked or not.  Used to determine if the "Select All" Checkbox
   * should be checked or not. 
   */
  isAllPeopleBeingTracked(): boolean {
    if (this.peopleInList.length == 0) {
      return false;
    }
    return this.peopleInList.every(personInList => this.peopleBeingTracked.has(personInList?.id));
  }

  /**
   * Starts the "Select All" / "Unselect All" Process based on the 
   * given select parameter.  This kicks off a background process
   * to perform the action, using the "isSelectingAllPeople" flag
   * to indicate if it is running or not and to disable the UI 
   * checkboxes.
   */
  public selectAllPeople(select: boolean): void {
    this.isSelectingAllPeople = true;
    this.selectAllPeopleAsync(select);
  }

  /**
   * This is the "Select All" / "Unselect All" Async Process that is kicked off.
   * This performs that process based on the given select parameter and then sets 
   * the "isSelectingAllPeople" flag back to false.
   */
  private async selectAllPeopleAsync(select: boolean): Promise<void> {
    const promises: Promise<void>[] = [];
    if (select) {
      this.peopleInList.forEach(personInList => {
        if (!this.peopleBeingTracked.has(personInList?.id)) {
          promises.push(this.startTrackingPerson(personInList));
        }
      });
    } else {
      this.peopleInList.forEach(personInList => {
        if (this.peopleBeingTracked.has(personInList?.id)) {
          promises.push(this.stopTrackingPerson(personInList));
        }
      });
    }
    await Promise.all(promises);

    // Update the "isSelectingAllPeople" flag to indicate the process is done and enable the UI checkboxes
    this.isSelectingAllPeople = false;
  }

  /**
   * Returns a unique ID that is used for the Popup Dialog for the 
   * given Instance (Asset / Person).
   */
  getIdForPopup(instance: ObjectInstance): string {
    return `mzInstancePopup_${instance?.id}`;
  }

  /**
   * Returns a List of all the actively tracked Instances (Assets / People).
   * Used to create a Popup Dialog for each one since they might be needed (Can display multiple at once).
   */
  getTrackedInstances(): ObjectInstance[] {
    const rv = this.assetsInZone.filter(da => this.assetsBeingTracked.has(da?.id));
    rv.push(...this.peopleInZone.filter(dp => this.peopleBeingTracked.has(dp?.id)));
    return rv;
  }

  /**
   * Gets the Hide Emitter for the given Instance (Asset / Person).
   * These are used to "hide" the Popup Dialog for the Instances.
   */
  getHideEmitter(instance: ObjectInstance): EventEmitter<boolean> {
    switch(instance?.object?.category) {
      case ObjectCategory.ASSET: {
        return this.assetsBeingTracked.get(instance?.id)?.popupHideEventEmitter;
      }
      case ObjectCategory.PEOPLE: {
        return this.peopleBeingTracked.get(instance?.id)?.popupHideEventEmitter;
      }
      default: {
        return null;
      }
    }
  }

  /**
   * Called when an Instance's Icon (Asset / Person) is clicked on the Map.
   */
  public onInstanceClicked(event: InstanceClickedEvent): void {
    // Hide the Popup Dialog if it is currently being displayed...
    this.getHideEmitter(event?.instance)?.emit(true);

    // Show the Popup Dialog at the mouse click
    $(`#${this.getIdForPopup(event?.instance)}`)?.css('left', `${event.event.stageX + $('#locatetab').outerWidth(true)}px`); // 10 is the Border Right padding added to the "locatetab" <div> element, but I don't know how to retrieve that / factor that into the width so hardcoding for now. (TJB)
    $(`#${this.getIdForPopup(event?.instance)}`)?.css('top', `${event.event.stageY}px`);
    $(`#${this.getIdForPopup(event?.instance)}`)?.show();

    /*
     * The below piece of code is needed since the top level "role='dialog'" <div> element used for the Dialog
     * is being padded with some extra invisible space underneath the Dialog.  This is preventing click events from
     * being detected on Icons that are visible underneath the Dialog since it was being blocked by the invisible part.
     * This resizes the outer "role='dialog'" <div> element to that of the inner "role='document'" <div> element. 
     * Hacky, but works.
     */
    $(`#${this.getIdForPopup(event?.instance)}`)?.css('height', $(`#${this.getIdForPopup(event?.instance)}_Document`)?.height() + 1);
  }

  /**
   * Event Handler called when the Asset Search Option is changed.  
   * This resets the search criteria and reloads the list unless the following:
   *   1) Already Searching Assets - No need.  Assets are not currently being displayed and will be searched for later.
   *   2) Search Value is blank - Again, no need. Already displaying all results
   */
  async onAssetSearchOptionChanged(): Promise<void> {
    // If Assets are being searched, then nothing needs to be done.
    if (this.isSearchingAssets) {
      return;
    }
    // If the Search Value for Asset's isn't specified, then we aren't displaying search results and nothing needs to be done.
    else if (this.assetSearchValue.length == 0) {
      return;
    }

    /*
     * Since the Asset Search Option is changing, reset the searchcriteria and reload the list.  
     * Use the "isAssetSearchOptionChanging" flag to disable UI elements to prevent issues if the user
     * decides to simulatenous track / search the Assets, etc. 
     */
    this.isAssetSearchOptionChanging = true; 
    this.assetSearchValue = "";
    this.refreshDisplayedAssetsInList();
    this.isAssetSearchOptionChanging = false;
  }

  /**
   * Event Handler called when the People Search Option is changed.  
   * This resets the search criteria and reloads the list unless the following:
   *   1) Already Searching People - No need.  People are not currently being displayed and will be searched for later.
   *   2) Search Value is blank - Again, no need. Already displaying all results
   */
  async onPeopleSearchOptionChanged(): Promise<void> {
    // If Assets are being searched, then nothing needs to be done.
    if (this.isSearchingPeople) {
      return;
    }
    // If the Search Value for Asset's isn't specified, then we aren't displaying search results and nothing needs to be done.
    else if (this.personSearchValue.length == 0) {
      return;
    }

    /*
     * Since the People Search Option is changing, reset the searchcriteria and reload the list.  
     * Use the "isPeopleSearchOptionChanging" flag to disable UI elements to prevent issues if the user
     * decides to simulatenous track / search the Assets, etc. 
     */
    this.isPeopleSearchOptionChanging = true; 
    this.personSearchValue = "";
    this.refreshDisplayedPeopleInList();
    this.isPeopleSearchOptionChanging = false;
  }

  onPageChange(pageNumber?: number): void {
      // Validate page number
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }
  
      // Avoid redundant reloads
      if (this.currentPage === pageNumber) {
        return;
      }
  
      this.currentPage = pageNumber;
      // Fetch the requested page from the server
      //this.refreshDisplayedTagsInList();
      this.refreshDisplayedAssetsInList();
      this.refreshDisplayedPeopleInList();
  }

    onZonePageChange(pageNumber?: number): void {
      // Validate page number
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }
  
      // Avoid redundant reloads
      if (this.currentPageZones === pageNumber) {
        return;
      }
  
      this.currentPageZones = pageNumber;
      // Fetch the requested page from the server
      this.refreshDisplayedZonesInList();

  }


}

/**
 * Class designed to hold the Web Socket subscription information
 * for the Assets / People being tracked.  This also holds the
 * "Hide" Event Emitter used for the Popup Dialogs since it will
 * only be needed if the Instances are actively tracked.
 */
class WsConnection {
  wsSubject: Subject<MessageEvent>;
  wsSubscription: Subscription;

  popupHideEventEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
}

/**
 * Different Types of Searches
 */
enum SearchType {
  /** Search by Instance Name */
  NAME = "NAME",
  /** Search by Property Data Value */
  PROPERTY = "PROPERTY"
}

/**
 * Search Option for Instances (Assets / People)
 */
class SearchOption {
  /** Search Type - NAME / PROPERTY */
  searchType: SearchType;
  /** Property being search by if the Search Type is PROPERTY */
  property: ObjectProperty;

  constructor(property?: ObjectProperty) {
    this.property = property;
    this.searchType = this.property == null? SearchType.NAME : SearchType.PROPERTY;
  }
}
